import * as ko from "knockout";
import * as _ from "lodash";
import * as Constants from "../common/constants";
import * as EventUtil from "../common/eventUtil";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as Util from "../common/util";
import { ICustomerDocument } from "../data/documentLibraryData";
import { IReportDefinition } from "../data/reportData";
import { ITemplateAccessRoleTemplate, ITemplateAccessRoleTemplateObservable } from "../data/templateAccessRoleData";
import * as TemplateData from "../data/templateData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";
import { ILanguage, ITranslatedText } from "../interfaces/translations";

// ==================================================================
// interfaces
// ==================================================================

interface ICustomerDocumentObservable {
    ID: KnockoutObservable<number>;
    StorageKey: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
    Description: KnockoutObservable<string>;
    FileName: KnockoutObservable<string>;
    TempFileKey: KnockoutObservable<string>;
}

interface ITemplateLinkedCustomerDocumentObservable {
    TemplateCustomerDocumentID: KnockoutObservable<number>;
    CustomerDocumentID: KnockoutObservable<number>;
    StorageKey: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
    Description: KnockoutObservable<string>;
    FileName: KnockoutObservable<string>;
}

// view model for the header fields, like name, target, is active, etc.
interface IViewModelHeader {
    ID: KnockoutObservable<number>;

    // editable fields
    TemplateName: KnockoutObservable<string>;
    TargetTemplateID: KnockoutObservable<number>;
    IsActive: KnockoutObservable<boolean>;
    CanChangeTargetTemplate: KnockoutObservable<boolean>;
    TargetTemplates: KnockoutObservableArray<TemplateData.ITemplateMasterTarget>;
    TargetTemplateName: KnockoutObservable<string>;
    // error flags & lookups

    TemplateNameMissing: KnockoutObservable<boolean>;
    TargetTemplateIDMissing: KnockoutObservable<boolean>;

    // events
    toggleIsActive: () => void;
}

interface ITranslatedTextObservable {
    LanguageID: number;
    LanguageName: string;
    TranslatedText: KnockoutObservable<string>;
}

// represents a top level navigation item displayed in the tree
interface INavigationObservable {
    ID: KnockoutObservable<number>;
    TemplateFormID: KnockoutObservable<number>;
    NavigationTypeID: KnockoutObservable<number>;
    NavigationName: KnockoutObservable<string>;
    NavigationNameTranslations: KnockoutObservableArray<ITranslatedTextObservable>;
    NavigationIcon: KnockoutObservable<string>;
    SortOrder: KnockoutObservable<number>;
    IsActive: KnockoutObservable<boolean>;

    // forms that can added under the navigation item
    ListItems: KnockoutObservableArray<number>;

    // lists that are under the navigation item
    Lists: KnockoutObservableArray<IListObservable>;

    // computed functions
    typeIsForm?: KnockoutComputed<boolean>;
    typeIsList?: KnockoutComputed<boolean>;
    typeIsReport?: KnockoutComputed<boolean>;
    numberOfLists?: KnockoutComputed<number>;
    rowStyle?: KnockoutComputed<string>;

    // if true, then any child items are visible
    IsExpanded: KnockoutObservable<boolean>;

    // if true, then this item is being edited
    IsSelected: KnockoutObservable<boolean>;

    // if true, show action buttons for this item
    ShowActions: KnockoutObservable<boolean>;

}

// represents a list item displayed in the tree
interface IListObservable {
    ID: KnockoutObservable<number>;
    ListName: KnockoutObservable<string>;
    HasForm: KnockoutObservable<boolean>;
    HasAttributes: KnockoutObservable<boolean>;
    SortOrder: KnockoutObservable<number>;
    IsActive: KnockoutObservable<boolean>;

    /// lists that are children of this list.  These are lists that get
    /// created automatically when an appointment is created from
    /// this template.
    Children: KnockoutObservableArray<IListObservable>;

    /// these are forms that can be added to this list.
    ListItems: KnockoutObservableArray<number>;

    /// if HasForm is true, this will contain the ID of the
    /// form associated with the list item.
    FormID: KnockoutObservable<number>;

    /// if HasAttributes is true, this will contain the ID of the
    /// form to use for the attributes.
    AttributeFormID: KnockoutObservable<number>;

    // if true, then any child items are visible
    IsExpanded: KnockoutObservable<boolean>;

    // if true, then this item is being edited
    IsSelected: KnockoutObservable<boolean>;

    // if true, show action buttons for this item
    ShowActions: KnockoutObservable<boolean>;

    typeIsForm?: KnockoutComputed<boolean>;
    typeIsList?: KnockoutComputed<boolean>;
    numberOfLists?: KnockoutComputed<number>;
    rowStyle?: KnockoutComputed<string>;
}

// used when selecting what forms can be created under
// a navigation or list
interface IListItemObservable {
    TemplateFormID: KnockoutObservable<number>;
    TemplateFormName: KnockoutObservable<string>;
    TemplateFormDescription: KnockoutObservable<string>;
    Selected: KnockoutObservable<boolean>;
    Visible: KnockoutComputed<boolean>;
}

// fields for editing a single navigation item
interface INavigationDetail {
    ID: KnockoutObservable<number>;

    // editable fields
    TemplateFormID: KnockoutObservable<number>;
    NavigationTypeID: KnockoutObservable<number>;
    NavigationName: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
    ListForms: KnockoutObservableArray<IListItemObservable>; // forms that can be added under this navigation

    searchFormName: KnockoutObservable<string>;
    searchResultHasForms?: KnockoutComputed<boolean>;
    // values for drop down/multi check fields
    NavigationTypes: KnockoutObservableArray<Data.ICodeModel<number>>;
    NavigationForms: KnockoutObservableArray<TemplateData.ITemplateMasterForm>;

    // error messages
    TemplateFormIDMissing: KnockoutObservable<boolean>;
    NavigationTypeIDMissing: KnockoutObservable<boolean>;
    NavigationNameMissing: KnockoutObservable<boolean>;
    ListFormsMissing: KnockoutObservable<boolean>;

    // computed functions
    typeIsForm?: KnockoutComputed<boolean>;
    typeIsList?: KnockoutComputed<boolean>;
    typeIsReport?: KnockoutComputed<boolean>;

    // events
    onClose: () => void;
    onDelete: () => void;
    onSave: () => void;
    onSaveAndClose: () => void;
    onUndoDelete: () => void;

}

// fields for editing a single list item
interface IListDetail {
    ID: KnockoutObservable<number>;

    // editable fields
    ListName: KnockoutObservable<string>;
    HasAttributes: KnockoutObservable<boolean>;
    IsActive: KnockoutObservable<boolean>;
    ListItems: KnockoutObservableArray<IListItemObservable>; // forms that can be added under this navigation
    ListTypeID: KnockoutObservable<string>;

    /// if HasForm is true, this will contain the ID of the
    /// form associated with the list item.
    FormID: KnockoutObservable<number>;

    /// if HasAttributes is true, this will contain the ID of the
    /// form to use for the attributes.
    AttributeFormID: KnockoutObservable<number>;

    // values for drop down/multi check fields
    ListTypes: KnockoutObservableArray<string>;
    // forms used to populate FormID drop down box
    ListForms: KnockoutObservableArray<TemplateData.ITemplateMasterForm>;

    searchListItemFormName: KnockoutObservable<string>;

    // error messages
    FormIDMissing: KnockoutObservable<boolean>;
    ListTypeIDMissing: KnockoutObservable<boolean>;
    ListNameMissing: KnockoutObservable<boolean>;
    ListFormsMissing: KnockoutObservable<boolean>;

    // computed functions
    typeIsForm?: KnockoutComputed<boolean>;
    typeIsList?: KnockoutComputed<boolean>;
    searchResultReturnsForms?: KnockoutComputed<boolean>;
    // events
    onClose: () => void;
    onDelete: () => void;
    onSave: () => void;
    onSaveAndClose: () => void;
    onUndoDelete: () => void;
}

interface ITemplateSettingObservable {
    ID: KnockoutObservable<number>;
    TemplateSettingTypeID: KnockoutObservable<number>;
    SettingKey: KnockoutObservable<string>;
    Value: KnockoutObservable<string>;
    SettingType: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
}

// All of the fields in the Settings section
interface ISettingsDetail {
    NotifyUserOnAssignment: KnockoutObservable<boolean>;
    ReportID: KnockoutObservable<number|undefined>;
    AllReports: KnockoutObservableArray<IReportDefinition>;
    LinkedCustomerDocuments: KnockoutObservableArray<ITemplateLinkedCustomerDocumentObservable>;
    SelectedCustomerDocument: KnockoutObservable<ICustomerDocumentObservable>;
    UnselectedCustomerDocuments?: KnockoutComputed<ICustomerDocumentObservable[]>;
    AllCustomerDocuments: KnockoutObservableArray<ICustomerDocumentObservable>;
    CustomerDocumentDownloadUrl: string;
    TemplateSecurityEnabled: KnockoutObservable<boolean>;
    TemplateRoles: KnockoutObservableArray<ITemplateAccessRoleTemplateObservable>;
    SingleInstanceSettings: KnockoutObservableArray<ITemplateSettingObservable>;
    ReportEmailSettings: KnockoutObservableArray<ITemplateSettingObservable>;
    ReportEmailSettingTypes: KnockoutObservableArray<TemplateData.ITemplateSettingType>;
    ReportEmailSettingTypeID: KnockoutObservable<number>;
    ReportEmailSettingValue: KnockoutObservable<string>;
    TemplateID: KnockoutObservable<number>;

    // Mobile Task List settings
    MobileTaskSettingsList: KnockoutObservableArray<ITemplateSettingObservable>;
    MobileTaskSettingPath: KnockoutObservable<string>;
    MobileSettingTypes: TemplateData.ITemplateSettingType[];

    inValidEntry: KnockoutObservable<boolean>;
    linkDocument: () => void;
    removeLinkedDocument: (doc: ITemplateLinkedCustomerDocumentObservable) => void;
    addReportEmailSetting: () => void;
    removeReportEmailSetting: (index: KnockoutObservable<number>) => void;
    addPathToMobileSettingList: () => void;
    removeMobileTaskSettingFromList: (index: KnockoutObservable<number>) => void;
}

interface ITranslationDetail {
    TranslationFieldTextSearch: KnockoutObservable<string>;
    TranslationLanguageIDSearch: KnockoutObservable<number>;
    TranslationLanguages: ILanguage[];
    Navigations: () => KnockoutObservableArray<INavigationObservable>;
    getFilteredTranslatedText: (navigationObservable: INavigationObservable) => ITranslatedTextObservable[];
}

// view model that contains the field for editing individual
// navigation or list items
interface IViewModelDetail {
    NavigationVisible: KnockoutObservable<boolean>;
    Navigation: INavigationDetail;
    // reference to the item being edited
    NavigationObservable?: INavigationObservable;
    // the state of the data when first loaded, to tell if something changed
    NavigationOnLoad: string;

    ListVisible: KnockoutObservable<boolean>;
    List: IListDetail;
    // reference to the item being edited
    ListObservable?: IListObservable;
    // the state of the data when first loaded, to tell if something changed
    ListOnLoad: string;

    SettingsVisibleDetail: KnockoutObservable<boolean>;
    Settings: ISettingsDetail;

    TranslationsVisibleDetail: KnockoutObservable<boolean>;
    Translations: ITranslationDetail;
}

// view model for showing the template tree structure
interface IViewModelTree {
    Navigations: KnockoutObservableArray<INavigationObservable>;
    onEditNavigation: (navigationObservable: INavigationObservable) => void;
    onAddNavigation: () => void;
    onAddNavigationList: (navigationObservable: INavigationObservable) => void;
    onMoveNavigationUp: (navigationObservable: INavigationObservable) => void;
    onMoveNavigationDown: (navigationObservable: INavigationObservable) => void;
    onNavigationShowActions: (navigationObservable: INavigationObservable) => void;
    onNavigationHideActions: (navigationObservable: INavigationObservable) => void;
    onEditList: (listObservable: IListObservable) => void;
    onNavigationToggleExpanded: (navigationObservable: INavigationObservable) => void;
    onAddList: (listObservable: IListObservable) => void;
    onMoveListUp: (listObservable: IListObservable) => void;
    onMoveListDown: (listObservable: IListObservable) => void;
    onListToggleExpanded: (listObservable: IListObservable) => void;
    onListShowActions: (listObservable: IListObservable) => void;
    onListHideActions: (listObservable: IListObservable) => void;
    AppointmentLabel: KnockoutObservable<string>;
    onShowSettings: () => void;
    onShowTranslations: () => void;

    SettingsVisibleTree: KnockoutObservable<boolean>;
    TranslationsVisibleTree: KnockoutObservable<boolean>;
}

// ==================================================================
// template Controller
// ==================================================================
export class TemplateController implements IController {

    // -------------------------------------------------------------------------
    // class properties
    // -------------------------------------------------------------------------

    private container: JQuery;
    private viewModelHeader: IViewModelHeader;
    private viewModelTree: IViewModelTree;
    private viewModelDetail: IViewModelDetail;
    private templateID: number;
    private fieldsOnLoad: string;

    private LISTTYPE_FORM = "Form";
    private LISTTYPE_LIST = "List";

    // -------------------------------------------------------------------------
    // constructor
    // -------------------------------------------------------------------------
    constructor() {

        this.viewModelHeader = this.createViewModelHeader();
        this.viewModelTree = this.createViewModelTree();
        this.viewModelDetail = this.createViewModelDetail();
    }

    // -------------------------------------------------------------------------
    // iController implementation
    // -------------------------------------------------------------------------

    public render(container: JQuery): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;

        ko.applyBindings(this.viewModelHeader, container.find("div[data-id='headerContainer']")[0]);
        ko.applyBindings(this.viewModelTree, container.find("div[data-id='treeContainer']")[0]);
        ko.applyBindings(this.viewModelDetail, container.find("div[data-id='detailContainer']")[0]);

        this.container.find("a[data-toggle='popover']").popover({ html: true });

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        if (params != null && params.length === 1) {
            this.templateID = params[0];
        } else {
            this.templateID = 0;
        }

        return this.loadRecord(this.templateID);
    }

    public haveFieldsChanged(): boolean {
        if (this.hasDetailDataChanged()) {
            // there are unsaved changes in the detail area
            return false;
        }

        const fieldValues: string = ko.toJSON(this.getTemplate());
        return fieldValues !== this.fieldsOnLoad;
    }

    public height(height: number): void {
        if (height > 150) {
            // set the min height of the detail area, so the containing box always extends to the bottom of the visible page
            this.container.find("div[data-id='detailContainer']").children(".tree-detail-container").css("min-height", height - 150);
        }
    }

    public appRouterID(params: any[]): string {
        let routerID: string = "Template";
        if (params != null && params.length === 1) {
            routerID += params[0];
        }
        return routerID;
    }

    // returns a title for the page
    public title(): string {
        return `${Constants.LABEL_APPOINTMENT} Template`;
    }

    public subTitle(): string {
        let subTitle: string = "";
        if (this.templateID == null) {
            subTitle = "New";
        } else {
            subTitle = this.viewModelHeader.TemplateName();
        }
        return subTitle;
    }

    public save(): JQueryPromise<any> {
        const me: TemplateController = this;
        const app: IApplication = Global.app();
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        let waitContainer: JQuery;

        if (!this.isHeaderValid()) {
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
            deferred.reject();
            return deferred.promise();
        }

        waitContainer = UI.showWaitIndicator(this.container);
        TemplateData.Save(this.getTemplate())
            .done((response: Data.IResponse<TemplateData.ITemplateMaster>) => {
                let currentID: number;
                if (response.Success) {
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);

                    currentID = me.viewModelHeader.ID();
                    me.clearSearch();
                    me.updateViewModel(response.Payload);

                    // hide the detail area if its visible
                    me.viewModelDetail.NavigationVisible(false);
                    me.viewModelDetail.ListVisible(false);

                    if (currentID <= 0) {
                        // add a new record, so update the URL
                        me.updateURL(response.Payload.Template.ID, response.Payload.Template.TemplateName);
                    } else {
                        // updating an existing record
                        app.pageManager().refreshCurrentPageTitle();
                    }
                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        return deferred.promise();
    }

    // -------------------------------------------------------------------------
    // create view model methods
    // -------------------------------------------------------------------------
    // private methods
    private createViewModelHeader(): IViewModelHeader {
        const me: TemplateController = this;
        const vm: IViewModelHeader = {
            ID: ko.observable<number>(),
            TemplateName: ko.observable<string>(),
            TargetTemplateID: ko.observable<number>(),
            IsActive: ko.observable<boolean>(),
            CanChangeTargetTemplate: ko.observable<boolean>(),
            TargetTemplates: ko.observableArray<TemplateData.ITemplateMasterTarget>(),
            TargetTemplateName: ko.observable<string>(),
            TemplateNameMissing: ko.observable<boolean>(false),
            TargetTemplateIDMissing: ko.observable<boolean>(false),

            toggleIsActive(): void {
                let message: string;

                if (this.IsActive()) {
                    message = "Are you sure you want to make this template inactive?";
                } else {
                    message = "Are you sure you want to make this template active?";
                }
                UI.showConfirmDialog(
                    message,
                    () => {
                        me.toggleIsActive();
                    });
            },

        };

        return vm;
    }

    private createViewModelTree(): IViewModelTree {
        const me: TemplateController = this;

        return {
            Navigations: ko.observableArray<INavigationObservable>(),
            AppointmentLabel: ko.observable(Constants.LABEL_APPOINTMENT),

            onEditNavigation(navigationObservable: INavigationObservable): void {
                me.confirmDetailChange(() => {
                    me.editNavigation(navigationObservable);
                });
            },
            onAddNavigation(): void {
                me.confirmDetailChange(() => {
                    me.addNavigation();
                });
            },
            onAddNavigationList(navigationObservable: INavigationObservable): void {
                me.confirmDetailChange(() => {
                    me.addListItemToNavigation(navigationObservable);
                });
            },
            onMoveNavigationUp(navigationObservable: INavigationObservable): void {
                me.moveNavigationItem(navigationObservable, true);
            },
            onMoveNavigationDown(navigationObservable: INavigationObservable): void {
                me.moveNavigationItem(navigationObservable, false);
            },
            onNavigationShowActions(navigationObservable: INavigationObservable): void {
                navigationObservable.ShowActions(true);
            },
            onNavigationHideActions(navigationObservable: INavigationObservable): void {
                navigationObservable.ShowActions(false);
            },
            onEditList(listObservable: IListObservable): void {
                me.confirmDetailChange(() => {
                    me.editList(listObservable);
                });
            },
            onNavigationToggleExpanded(navigationObservable: INavigationObservable): void {
                navigationObservable.IsExpanded(!navigationObservable.IsExpanded());
            },
            onAddList(listObservable: IListObservable): void {
                me.confirmDetailChange(() => {
                    me.addListItemToList(listObservable);
                });
            },
            onMoveListUp(listObservable: IListObservable): void {
                me.moveListItem(listObservable, true);
            },
            onMoveListDown(listObservable: IListObservable): void {
                me.moveListItem(listObservable, false);
            },
            onListToggleExpanded(listObservable: IListObservable): void {
                listObservable.IsExpanded(!listObservable.IsExpanded());
            },
            onListShowActions(listObservable: IListObservable): void {
                listObservable.ShowActions(true);
            },
            onListHideActions(listObservable: IListObservable): void {
                listObservable.ShowActions(false);
            },

            onShowSettings: () => {
                me.confirmDetailChange(() => {
                    me.unselectAllNavigation();
                    me.viewModelDetail.ListVisible(false);
                    me.viewModelDetail.NavigationVisible(false);
                    me.viewModelDetail.SettingsVisibleDetail(true);
                    me.viewModelDetail.TranslationsVisibleDetail(false);
                    this.viewModelTree.SettingsVisibleTree(true);
                    this.viewModelTree.TranslationsVisibleTree(false);
                });
            },
            onShowTranslations: () => {
                me.confirmDetailChange(() => {
                    me.unselectAllNavigation();
                    me.viewModelDetail.ListVisible(false);
                    me.viewModelDetail.NavigationVisible(false);
                    me.viewModelDetail.SettingsVisibleDetail(false);
                    me.viewModelDetail.TranslationsVisibleDetail(true);
                    this.viewModelTree.SettingsVisibleTree(false);
                    this.viewModelTree.TranslationsVisibleTree(true);
                });
            },

            SettingsVisibleTree: ko.observable(false),
            TranslationsVisibleTree: ko.observable(false),
        };
    }

    private createViewModelDetail(): IViewModelDetail {
        const me: TemplateController = this;

        const app: IApplication = Global.app();
        const languages = app.userSession ? app.userSession.Languages : [];

        const viewModelDetail: IViewModelDetail = {
            NavigationVisible: ko.observable(false),
            Navigation: {
                ID: ko.observable<number>(),
                TemplateFormID: ko.observable<number>(),
                NavigationTypeID: ko.observable<number>(),
                NavigationName: ko.observable<string>(),
                IsActive: ko.observable<boolean>(),
                ListForms: ko.observableArray<IListItemObservable>(),
                searchFormName: ko.observable<string>(),
                NavigationTypes: ko.observableArray<Data.ICodeModel<number>>(),
                NavigationForms: ko.observableArray<TemplateData.ITemplateMasterForm>(),
                TemplateFormIDMissing: ko.observable<boolean>(false),
                NavigationTypeIDMissing: ko.observable<boolean>(false),
                NavigationNameMissing: ko.observable<boolean>(false),
                ListFormsMissing: ko.observable<boolean>(false),
                // events
                onClose(): void {
                    if (me.hasDetailDataChanged()) {
                        UI.showConfirmDialog(Constants.PHRASE_ITEMUNSAVEDCHANGES,
                            () => {
                                me.unselectAllNavigation();
                                me.viewModelDetail.NavigationVisible(false);
                            });
                    } else {
                        me.unselectAllNavigation();
                        me.viewModelDetail.NavigationVisible(false);
                    }
                },
                onDelete(): void {
                    UI.showConfirmDialog("Are you sure you want to delete the navigation item '"
                        + this.NavigationName() + "' and any child items?",
                        () => {
                            me.setNavigationIsActive(false);
                            UI.showNotificationMessage(Constants.PHRASE_ITEMDELETED, UI.messageTypeValues.success);
                        });
                },
                onUndoDelete(): void {
                    me.setNavigationIsActive(true);
                },
                onSave(): void {
                    me.clearSearch();
                    me.saveNavigation();
                },
                onSaveAndClose(): void {
                    me.clearSearch();
                    if (me.saveNavigation()) {
                        me.unselectAllNavigation();
                        me.viewModelDetail.NavigationVisible(false);
                    }
                },
            },
            NavigationOnLoad: "",
            ListVisible: ko.observable(false),
            List: {
                ID: ko.observable<number>(),

                // editable fields
                ListName: ko.observable<string>(),
                HasAttributes: ko.observable<boolean>(),
                IsActive: ko.observable<boolean>(),
                ListItems: ko.observableArray<IListItemObservable>(),
                ListTypeID: ko.observable<string>(),

                /// if HasForm is true, this will contain the ID of the
                /// form associated with the list item.
                FormID: ko.observable<number>(),

                /// if HasAttributes is true, this will contain the ID of the
                /// form to use for the attributes.
                AttributeFormID: ko.observable<number>(),

                // values for drop down/multi check fields
                ListTypes: ko.observableArray<string>([me.LISTTYPE_FORM, me.LISTTYPE_LIST]),
                ListForms: ko.observableArray<TemplateData.ITemplateMasterForm>(),

                searchListItemFormName: ko.observable<string>(),
                // error messages
                FormIDMissing: ko.observable<boolean>(false),
                ListTypeIDMissing: ko.observable<boolean>(false),
                ListNameMissing: ko.observable<boolean>(false),
                ListFormsMissing: ko.observable<boolean>(false),

                // events
                onClose(): void {
                    if (me.hasDetailDataChanged()) {
                        UI.showConfirmDialog(Constants.PHRASE_ITEMUNSAVEDCHANGES,
                            () => {
                                me.unselectAllNavigation();
                                me.viewModelDetail.ListVisible(false);
                            });
                    } else {
                        me.unselectAllNavigation();
                        me.viewModelDetail.ListVisible(false);
                    }
                },
                onDelete(): void {
                    UI.showConfirmDialog("Are you sure you want to delete the list item '" + this.ListName() + "' and any child items?",
                        () => {
                            me.setListIsActive(false);
                            UI.showNotificationMessage(Constants.PHRASE_ITEMDELETED, UI.messageTypeValues.success);
                        });
                },
                onUndoDelete(): void {
                    me.setListIsActive(true);
                },
                onSave(): void {
                    me.clearSearch();
                    me.saveList();
                },
                onSaveAndClose(): void {
                    me.clearSearch();
                    if (me.saveList()) {
                        me.unselectAllNavigation();
                        me.viewModelDetail.ListVisible(false);
                    }
                },

            },
            ListOnLoad: "",
            SettingsVisibleDetail: ko.observable(false),
            Settings: {
                TemplateSecurityEnabled: ko.observable(false),
                TemplateRoles: ko.observableArray(),
                AllReports: ko.observableArray(),
                ReportID: ko.observable(),
                TemplateID: ko.observable(),
                CustomerDocumentDownloadUrl: app.webServiceBaseURL + "/api/DocumentLibrary/Download/",
                NotifyUserOnAssignment: ko.observable<boolean>(),
                SelectedCustomerDocument: ko.observable(),
                AllCustomerDocuments: ko.observableArray(),
                LinkedCustomerDocuments: ko.observableArray(),
                SingleInstanceSettings: ko.observableArray(),
                ReportEmailSettings: ko.observableArray(),
                ReportEmailSettingTypes: ko.observableArray(),
                ReportEmailSettingTypeID: ko.observable(),
                ReportEmailSettingValue: ko.observable(),

                MobileTaskSettingsList: ko.observableArray(),
                MobileTaskSettingPath: ko.observable(),
                MobileSettingTypes: [],
                inValidEntry: ko.observable(false),
                linkDocument: () => {
                    if (viewModelDetail.Settings.SelectedCustomerDocument()) {
                        const doc = viewModelDetail.Settings.SelectedCustomerDocument();
                        const linkedDoc: ITemplateLinkedCustomerDocumentObservable = {
                            TemplateCustomerDocumentID: ko.observable(0),
                            CustomerDocumentID: ko.observable(doc.ID()),
                            Description: ko.observable(doc.Description()),
                            FileName: ko.observable(doc.FileName()),
                            IsActive: ko.observable(doc.IsActive()),
                            StorageKey: ko.observable(doc.StorageKey()),
                        };

                        viewModelDetail.Settings.LinkedCustomerDocuments.push(linkedDoc);
                        viewModelDetail.Settings.SelectedCustomerDocument(null);
                    }
                },
                removeLinkedDocument: (doc: ITemplateLinkedCustomerDocumentObservable) => {
                    UI.showConfirmDialog("Are you sure you want to remove this document?", () => {
                        if (doc.TemplateCustomerDocumentID() > 0) {
                            // already in the db, so soft-delete
                            doc.IsActive(false);
                        } else {
                            // not yet in the db so just remove
                            viewModelDetail.Settings.LinkedCustomerDocuments.remove(doc);
                        }
                    });
                },
                addReportEmailSetting: () => {
                    const settings = me.viewModelDetail.Settings;
                    if (settings.ReportEmailSettingTypeID() && settings.ReportEmailSettingValue()) {
                        const settingKey: any = settings.ReportEmailSettingTypes().filter((x) => x.ID === settings.ReportEmailSettingTypeID());
                        const templateSetting: ITemplateSettingObservable = {
                            ID: ko.observable(0),
                            TemplateSettingTypeID: ko.observable(settings.ReportEmailSettingTypeID()),
                            SettingKey: ko.observable(settingKey[0].SettingKey),
                            Value: ko.observable(settings.ReportEmailSettingValue()),
                            SettingType: ko.observable("string"),
                            IsActive: ko.observable(true),
                        };
                        settings.ReportEmailSettings.push(templateSetting);
                        settings.ReportEmailSettingTypeID(null);
                        settings.ReportEmailSettingValue("");
                    }
                },
                removeReportEmailSetting: (index: KnockoutObservable<number>): any => {
                    const selectedSetting: ITemplateSettingObservable = me.viewModelDetail.Settings.ReportEmailSettings()[index()];
                    UI.showConfirmDialog("Are you sure you want to delete \"" + selectedSetting.SettingKey() + "\"?",
                        () => { me.DeleteReportEmailSetting(selectedSetting); });
                },
                addPathToMobileSettingList: () => {
                    const newSetting = me.viewModelDetail.Settings;
                    if (!Util.isNullOrSpace(newSetting.MobileTaskSettingPath()) &&
                        !newSetting.MobileTaskSettingsList().some( (x) => x.Value().toLowerCase() === newSetting.MobileTaskSettingPath().toLowerCase()
                                                                          && x.IsActive() === true)) {
                        me.viewModelDetail.Settings.inValidEntry(false);
                        const settingKey: any = newSetting.MobileSettingTypes.filter((x) => x.SettingKey === Constants.TEMPLATE_MOBILE_SETTING_KEY_FIELD_PATH &&
                                                                                              x.SettingGroup === Constants.TEMPLATE_SETTING_GROUP_MOBILE_TASK_LIST);
                        const templateSetting: ITemplateSettingObservable = {
                            ID: ko.observable(0),
                            TemplateSettingTypeID: ko.observable(settingKey[0].ID),
                            SettingKey: ko.observable(settingKey[0].SettingKey),
                            Value: ko.observable(newSetting.MobileTaskSettingPath()),
                            SettingType: ko.observable("string"),
                            IsActive: ko.observable(true),
                        };
                        newSetting.MobileTaskSettingsList.push(templateSetting);
                        newSetting.MobileTaskSettingPath("");
                    } else {
                        me.viewModelDetail.Settings.inValidEntry(true);
                    }
                },
                removeMobileTaskSettingFromList: (index: KnockoutObservable<number>): any => {
                    const selectedSetting: ITemplateSettingObservable = me.viewModelDetail.Settings.MobileTaskSettingsList()[index()];
                    UI.showConfirmDialog( "Are you sure you want to delete \"" + selectedSetting.SettingKey() + "\"?",
                        () => {me.DeleteMobileSetting(selectedSetting); });
                },
            },
            TranslationsVisibleDetail: ko.observable(false),
            Translations: {
                TranslationFieldTextSearch: ko.observable(""),
                TranslationLanguageIDSearch: ko.observable(0),
                TranslationLanguages: languages,
                Navigations: () => {
                    return me.viewModelTree.Navigations;
                },
                getFilteredTranslatedText(navigationObservable: INavigationObservable): ITranslatedTextObservable[] {
                    const searchText = this.TranslationFieldTextSearch() ? this.TranslationFieldTextSearch().toLowerCase() : "";
                    return navigationObservable.NavigationNameTranslations().filter((navigationNameTranslations) => {
                        return (
                                    !this.TranslationLanguageIDSearch() ||
                                    navigationNameTranslations.LanguageID === this.TranslationLanguageIDSearch()
                                )
                                &&
                                (
                                    !this.TranslationFieldTextSearch() ||
                                    (navigationObservable.NavigationName() && navigationObservable.NavigationName().toLowerCase().indexOf(searchText) >= 0));
                    });
               },
            },
        };

        viewModelDetail.Navigation.typeIsForm = ko.pureComputed((): boolean => {
            return viewModelDetail.Navigation.NavigationTypeID() === TemplateData.NAVIGATIONTYPE_FORM;
        });

        viewModelDetail.Navigation.typeIsList = ko.pureComputed((): boolean => {
            return viewModelDetail.Navigation.NavigationTypeID() === TemplateData.NAVIGATIONTYPE_LIST;
        });

        viewModelDetail.Navigation.typeIsReport = ko.pureComputed((): boolean => {
            return viewModelDetail.Navigation.NavigationTypeID() === TemplateData.NAVIGATIONTYPE_REPORT;
        });

        viewModelDetail.List.typeIsForm = ko.pureComputed((): boolean => {
            return viewModelDetail.List.ListTypeID() === me.LISTTYPE_FORM;
        });

        viewModelDetail.List.typeIsList = ko.pureComputed((): boolean => {
            return viewModelDetail.List.ListTypeID() === me.LISTTYPE_LIST;
        });

        viewModelDetail.Navigation.searchResultHasForms = ko.pureComputed((): boolean => {
            return viewModelDetail.Navigation.ListForms().some((x) => x.Visible() === true);
        });

        viewModelDetail.List.searchResultReturnsForms = ko.pureComputed((): boolean => {
            return viewModelDetail.List.ListItems().some((x) => x.Visible() === true);
        });

        viewModelDetail.Settings.UnselectedCustomerDocuments = ko.pureComputed((): ICustomerDocumentObservable[] => {
            const selectedDocumentIDs: number[] = viewModelDetail.Settings.LinkedCustomerDocuments().map((doc) => doc.CustomerDocumentID());
            return viewModelDetail.Settings.AllCustomerDocuments().filter((doc) => {
                return selectedDocumentIDs.indexOf(doc.ID()) < 0;
            });
        });

        if (app.userSession) {
            const setting = UI.GetCustomerSetting(app.userSession.CustomerSettings, Constants.SETTING_KEY_TEMPLATE_ACCESS_SECURITY, "false");
            viewModelDetail.Settings.TemplateSecurityEnabled(setting === "true");
        }

        return viewModelDetail;
    }

    // -------------------------------------------------------------------------
    // validation methods
    // -------------------------------------------------------------------------

    private isHeaderValid(): boolean {
        let isValid: boolean = true;
        const header: IViewModelHeader = this.viewModelHeader;

        isValid = UI.setRequiredString(header.TemplateName, header.TemplateNameMissing) ? isValid : false;
        if (header.CanChangeTargetTemplate()) {
            isValid = UI.setRequiredNumber(header.TargetTemplateID, header.TargetTemplateIDMissing, true) ? isValid : false;
        } else {
            header.TargetTemplateIDMissing(false);
        }

        return isValid;
    }

    // -------------------------------------------------------------------------
    // load/update view model methods
    // -------------------------------------------------------------------------

    private loadRecord(id: number): JQueryPromise<any> {
        const me: TemplateController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        TemplateData.GetMaster(id)
            .done((response: Data.IResponse<TemplateData.ITemplateMaster>) => {
                if (response.Success) {
                    me.updateViewModel(response.Payload);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private updateViewModel(templateMaster: TemplateData.ITemplateMaster): void {
        this.updateViewModelHeader(templateMaster);
        this.updateViewModelTree(templateMaster);
        this.updateViewModelDetail(templateMaster);

        // remember the fields value so we can determine if they have changed
        this.fieldsOnLoad = ko.toJSON(this.getTemplate());
    }

    private updateViewModelHeader(templateMaster: TemplateData.ITemplateMaster): void {

        // drop down boxes must be updated first
        this.viewModelHeader.TargetTemplates.removeAll();
        this.viewModelHeader.TargetTemplates.push(
            { ID: 0, TemplateName: "(select target type)", URLFriendlyName: "", TemplateTypeName: "" });

        templateMaster.TargetTemplates.forEach((targetTemplate) => {
            this.viewModelHeader.TargetTemplates.push(targetTemplate);
        });

        this.viewModelHeader.ID(templateMaster.Template.ID);
        this.viewModelHeader.TemplateName(templateMaster.Template.TemplateName);
        this.viewModelHeader.TargetTemplateID(templateMaster.Template.TargetTemplateID);
        this.viewModelHeader.IsActive(templateMaster.Template.IsActive);
        this.viewModelHeader.CanChangeTargetTemplate(templateMaster.CanChangeTargetTemplate);
        this.viewModelHeader.TargetTemplateName(templateMaster.TargetTemplateName);
    }

    private updateViewModelTree(templateMaster: TemplateData.ITemplateMaster): void {
        const me: TemplateController = this;
        this.viewModelTree.Navigations.removeAll();
        templateMaster.Template.Navigations.forEach((navigation: TemplateData.ITemplateNavigation) => {
            me.viewModelTree.Navigations.push(me.createNavigationObservable(navigation));
        });
    }

    private updateViewModelDetail(templateMaster: TemplateData.ITemplateMaster): void {
        const me: TemplateController = this;

        me.viewModelDetail.Navigation.NavigationTypes(templateMaster.NavigationTypes);

        // values used when type is Navigation
        me.viewModelDetail.Navigation.ListForms.removeAll();
        me.viewModelDetail.Navigation.NavigationForms.removeAll();
        me.viewModelDetail.Navigation.NavigationForms.push({ ID: "", FormName: "(select form)", FormDescription: "" });
        templateMaster.NavigationForms.forEach((listForm: TemplateData.ITemplateMasterForm) => {
            const listItem: IListItemObservable = {
                TemplateFormID: ko.observable<number>(listForm.ID),
                TemplateFormName: ko.observable<string>(listForm.FormName),
                TemplateFormDescription: ko.observable<string>(listForm.FormDescription),
                Selected: ko.observable<boolean>(false),
                Visible: ko.pureComputed((): boolean => me.isVisible(me.viewModelDetail.Navigation.searchFormName, listItem)),
            };

            me.viewModelDetail.Navigation.ListForms.push(listItem);
            me.viewModelDetail.Navigation.NavigationForms.push(
                { ID: listForm.ID, FormName: listForm.FormName, FormDescription: listForm.FormDescription });
        });
        // values used when type is list
        me.viewModelDetail.List.ListItems.removeAll();
        me.viewModelDetail.List.ListForms.removeAll();
        me.viewModelDetail.List.ListForms.push({ ID: "", FormName: "(select form)", FormDescription: "" });
        templateMaster.NavigationForms.forEach((listForm: TemplateData.ITemplateMasterForm) => {
            const listItem: IListItemObservable = {
                TemplateFormID: ko.observable<number>(listForm.ID),
                TemplateFormName: ko.observable<string>(listForm.FormName),
                TemplateFormDescription: ko.observable<string>(listForm.FormDescription),
                Selected: ko.observable<boolean>(false),
                Visible: ko.pureComputed((): boolean => me.isVisible(me.viewModelDetail.List.searchListItemFormName, listItem)),
            };
            me.viewModelDetail.List.ListItems.push(listItem);

            me.viewModelDetail.List.ListForms.push(
                { ID: listForm.ID, FormName: listForm.FormName, FormDescription: listForm.FormDescription });
        });

        this.viewModelDetail.Settings.LinkedCustomerDocuments.removeAll();
        templateMaster.Template.LinkedCustomerDocuments.forEach((doc) => {
            this.viewModelDetail.Settings.LinkedCustomerDocuments.push(this.createLinkedCustomerDocumentObservable(doc));
        });

        this.viewModelDetail.Settings.AllCustomerDocuments.removeAll();
        templateMaster.AllCustomerDocuments.forEach((doc) => {
            this.viewModelDetail.Settings.AllCustomerDocuments.push(this.createCustomerDocumentObservable(doc));
        });

        this.viewModelDetail.Settings.NotifyUserOnAssignment(templateMaster.Template.NotifyUserOnAssignment);
        this.viewModelDetail.Settings.AllReports(templateMaster.AllReports);
        this.viewModelDetail.Settings.ReportID(templateMaster.Template.ReportID);
        this.viewModelDetail.Settings.TemplateRoles.removeAll();
        for (const r of templateMaster.Template.TemplateRoles) {
            this.viewModelDetail.Settings.TemplateRoles.push({
                ID: ko.observable(r.ID),
                TemplateID: ko.observable(templateMaster.Template.ID),
                IsActive: ko.observable(r.IsActive),
                TemplateAccessRoleID: ko.observable(r.TemplateAccessRoleID),
                TemplateAccessRoleName: ko.observable(r.TemplateAccessRoleName),
                TemplateName: ko.observable(r.TemplateName),
            });
        }

        this.viewModelDetail.Settings.SingleInstanceSettings.removeAll();
        this.viewModelDetail.Settings.ReportEmailSettings.removeAll();
        for (const setting of templateMaster.TemplateSettings) {
            if (setting.IsSingleInstance) {
                this.viewModelDetail.Settings.SingleInstanceSettings.push(this.createTemplateSettingObservable(setting));
            } else if (setting.SettingGroup === Constants.TEMPLATE_SETTING_GROUP_EMAIL_RECIPIENTS) {
                this.viewModelDetail.Settings.ReportEmailSettings.push(this.createTemplateSettingObservable(setting));
            }
        }
        this.viewModelDetail.Settings.ReportEmailSettingTypes.removeAll();
        for (const settingType of templateMaster.TemplateSettingTypes) {
            if (settingType.SettingGroup === Constants.TEMPLATE_SETTING_GROUP_EMAIL_RECIPIENTS) {
                this.viewModelDetail.Settings.ReportEmailSettingTypes.push(settingType);
            }
        }
        this.viewModelDetail.Settings.MobileTaskSettingsList.removeAll();
        for ( const setting of templateMaster.TemplateSettings) {
            if (setting.SettingGroup === Constants.TEMPLATE_SETTING_GROUP_MOBILE_TASK_LIST) {
                this.viewModelDetail.Settings.MobileTaskSettingsList.push(this.createTemplateSettingObservable(setting));
            }
        }
        this.viewModelDetail.Settings.MobileSettingTypes.splice(0, 1);
        for (const settingType of templateMaster.TemplateSettingTypes) {
            if (settingType.SettingGroup === Constants.TEMPLATE_SETTING_GROUP_MOBILE_TASK_LIST) {
                this.viewModelDetail.Settings.MobileSettingTypes.push(settingType);
            }
        }
        this.viewModelDetail.Settings.MobileTaskSettingPath("");
        this.viewModelDetail.Settings.inValidEntry(false);

    }

    private createTemplateSettingObservable(templateSetting: TemplateData.ITemplateSetting): ITemplateSettingObservable {
        const settingObservable: ITemplateSettingObservable = {
            ID: ko.observable(templateSetting.ID),
            TemplateSettingTypeID: ko.observable(templateSetting.TemplateSettingTypeID),
            SettingKey: ko.observable(templateSetting.SettingKey),
            Value: ko.observable(templateSetting.Value),
            SettingType: ko.observable(templateSetting.SettingType),
            IsActive: ko.observable(templateSetting.IsActive),
        };

        return settingObservable;
    }

    private isVisible(searchword: KnockoutObservable<string>, listItem: IListItemObservable): boolean {
        let isValid: boolean;
        if (!searchword()) {
            isValid = true;
        } else {
            const searchStringIndex: number = listItem.TemplateFormName().toLowerCase().indexOf(searchword().toLowerCase());
            if (searchStringIndex < 0) {
                isValid = false;
            } else {
                isValid = true;
            }
        }
        return isValid;
    }
    private createNavigationObservable(navigation: TemplateData.ITemplateNavigation): INavigationObservable {
        const getFormTextTranslationObservables = (formTextTranslations: ITranslatedText[]): ITranslatedTextObservable[] => {
            const formTextTranslationObservables = this.createEmptyFormTextTranslations();
            if (formTextTranslations) {
                for (const formTextTranslationObservable of formTextTranslationObservables) {
                    const matchingFormTextTranslation = formTextTranslations.filter((formTextTranslation) =>
                                                    formTextTranslation.LanguageID === formTextTranslationObservable.LanguageID);
                    if (matchingFormTextTranslation.length > 0) {
                        formTextTranslationObservable.TranslatedText(matchingFormTextTranslation[0].TranslatedText);
                    }
                }
            }
            return formTextTranslationObservables;
        };

        const navigationObservable: INavigationObservable = {
            ID: ko.observable<number>(navigation.ID),
            TemplateFormID: ko.observable<number>(navigation.TemplateFormID),
            NavigationTypeID: ko.observable<number>(navigation.NavigationTypeID),
            NavigationName: ko.observable<string>(navigation.NavigationName),
            NavigationNameTranslations: ko.observableArray(getFormTextTranslationObservables(navigation.NavigationNameTranslations)),
            NavigationIcon: ko.observable<string>(navigation.NavigationIcon),
            SortOrder: ko.observable<number>(navigation.SortOrder),
            IsActive: ko.observable<boolean>(navigation.IsActive),
            IsExpanded: ko.observable<boolean>(true),
            ListItems: ko.observableArray<number>(this.createListItemIDs(navigation.ListItems)),
            Lists: ko.observableArray<IListObservable>(this.createListObservables(navigation.Lists)),
            IsSelected: ko.observable<boolean>(false),
            ShowActions: ko.observable<boolean>(false),
        };

        navigationObservable.typeIsForm = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === TemplateData.NAVIGATIONTYPE_FORM;
        });

        navigationObservable.typeIsList = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === TemplateData.NAVIGATIONTYPE_LIST;
        });

        navigationObservable.typeIsReport = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === TemplateData.NAVIGATIONTYPE_REPORT;
        });

        navigationObservable.numberOfLists = ko.pureComputed((): number => {
            return navigationObservable.Lists().length;
        });

        navigationObservable.rowStyle = ko.pureComputed((): string => {
            if (navigationObservable.IsSelected()) {
                return "tree-item tree-item-selected";
            } else {
                return "tree-item";
            }
        });

        return navigationObservable;
    }

    private createCustomerDocumentObservable(doc: ICustomerDocument): ICustomerDocumentObservable {
        return {
            ID: ko.observable(doc.ID),
            Description: ko.observable(doc.Description),
            FileName: ko.observable(doc.FileName),
            IsActive: ko.observable(doc.IsActive),
            StorageKey: ko.observable(doc.StorageKey),
            TempFileKey: ko.observable(doc.TempFileKey),
        };
    }

    private createLinkedCustomerDocumentObservable(doc: TemplateData.ITemplateLinkedCustomerDocument): ITemplateLinkedCustomerDocumentObservable {
        return {
            TemplateCustomerDocumentID: ko.observable(doc.TemplateCustomerDocumentID),
            CustomerDocumentID: ko.observable(doc.CustomerDocumentID),
            Description: ko.observable(doc.Description),
            FileName: ko.observable(doc.FileName),
            IsActive: ko.observable(doc.IsActive),
            StorageKey: ko.observable(doc.StorageKey),
        };
    }

    // return a list of TemplateFormIDs
    private createListItemIDs(templateListItems: TemplateData.ITemplateListItem[]): number[] {
        const listItemIDs: number[] = [];

        templateListItems.forEach((templateListItem: TemplateData.ITemplateListItem) => {
            listItemIDs.push(templateListItem.TemplateFormID);
        });

        return listItemIDs;
    }

    private createListObservables(templateLists: TemplateData.ITemplateList[]): IListObservable[] {
        const me: TemplateController = this;
        const listObservables: IListObservable[] = [];

        templateLists.forEach((templateList: TemplateData.ITemplateList) => {
            listObservables.push(me.createListObservable(templateList));
        });

        return listObservables;
    }

    private createListObservable(templateList: TemplateData.ITemplateList): IListObservable {
        const listObservable: IListObservable = {
            ID: ko.observable<number>(templateList.ID),
            ListName: ko.observable<string>(templateList.ListName),
            HasForm: ko.observable<boolean>(templateList.HasForm),
            HasAttributes: ko.observable<boolean>(templateList.HasAttributes),
            SortOrder: ko.observable<number>(templateList.SortOrder),
            IsActive: ko.observable<boolean>(templateList.IsActive),
            Children: ko.observableArray<IListObservable>(this.createListObservables(templateList.Children)),
            ListItems: ko.observableArray<number>(this.createListItemIDs(templateList.ListItems)),
            FormID: ko.observable<number>(templateList.FormID),
            AttributeFormID: ko.observable<number>(templateList.AttributeFormID),
            IsExpanded: ko.observable<boolean>(true),
            IsSelected: ko.observable<boolean>(false),
            ShowActions: ko.observable<boolean>(false),
        };

        listObservable.typeIsForm = ko.pureComputed((): boolean => {
            return listObservable.HasForm();
        });

        listObservable.typeIsList = ko.pureComputed((): boolean => {
            return !listObservable.HasForm();
        });

        listObservable.numberOfLists = ko.pureComputed((): number => {
            return listObservable.Children().length;
        });

        listObservable.rowStyle = ko.pureComputed((): string => {
            if (listObservable.IsSelected()) {
                return "tree-item tree-item-selected";
            } else {
                return "tree-item";
            }
        });

        return listObservable;
    }

    private toggleIsActive(): JQueryPromise<any> {
        const me: TemplateController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);
        const newIsActive: boolean = !this.viewModelHeader.IsActive();
        const request: TemplateData.ITemplateSetIsActiveRequest = {
            TemplateID: this.viewModelHeader.ID(),
            IsActive: newIsActive,
        };

        TemplateData.SetIsActive(request)
            .done((response: Data.IResponse<string>) => {
                if (response.Success) {
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                    me.viewModelHeader.IsActive(newIsActive);
                    // remember the fields value so we can determine if they have changed
                    me.fieldsOnLoad = ko.toJSON(me.getTemplate());
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve(true);
            });

        return deferred.promise();

    }

    // -------------------------------------------------------------------------
    // navigation edit functions
    // -------------------------------------------------------------------------

    // loads the specified navigation item into the detail screen for editing
    private editNavigation(navigationObservable: INavigationObservable): void {
        const navigation: INavigationDetail = this.viewModelDetail.Navigation;

        navigation.ID(navigationObservable.ID());

        navigation.TemplateFormID(navigationObservable.TemplateFormID());
        navigation.NavigationTypeID(navigationObservable.NavigationTypeID());
        navigation.NavigationName(navigationObservable.NavigationName());
        navigation.IsActive(navigationObservable.IsActive());

        // set the selected forms
        const templateFormIDs: number[] = navigationObservable.ListItems();
        navigation.ListForms().forEach((listItem: IListItemObservable) => {
            const selected: boolean = _.includes(templateFormIDs, listItem.TemplateFormID());
            listItem.Selected(selected);
        });

        // clear all errors
        navigation.NavigationNameMissing(false);
        navigation.NavigationTypeIDMissing(false);
        navigation.TemplateFormIDMissing(false);
        navigation.ListFormsMissing(false);

        // remember the object we are editing
        this.viewModelDetail.NavigationObservable = navigationObservable;
        // save current values so we can see if they changed
        this.viewModelDetail.NavigationOnLoad = ko.toJSON(this.viewModelDetail.Navigation);

        // update the UI
        this.unselectAllNavigation();
        navigationObservable.IsSelected(true);

        this.viewModelDetail.ListVisible(false);
        this.viewModelDetail.NavigationVisible(true);
        this.viewModelDetail.SettingsVisibleDetail(false);
        this.viewModelTree.SettingsVisibleTree(false);
        this.viewModelTree.TranslationsVisibleTree(false);

        this.container.find("div[data-id='detailContainer']").find("input:first").focus();
    }

    // saves the navigation item that is being edited in the detail page
    // into the main tree observable object.
    private saveNavigation(): boolean {
        if (this.viewModelDetail.NavigationObservable == null) {
            // this should not happen
            this.unselectAllNavigation();
            this.viewModelDetail.NavigationVisible(false);
            return true;
        }

        if (!this.isNavigationValid()) {
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
            return false;
        }

        const navigation: INavigationDetail = this.viewModelDetail.Navigation;
        const navigationObservable: INavigationObservable = this.viewModelDetail.NavigationObservable;

        navigationObservable.TemplateFormID(navigation.TemplateFormID());
        navigationObservable.NavigationTypeID(navigation.NavigationTypeID());
        navigationObservable.NavigationName(navigation.NavigationName());

        navigationObservable.ListItems.removeAll();
        navigation.ListForms().forEach((listForm: IListItemObservable) => {
            if (listForm.Selected()) {
                navigationObservable.ListItems.push(listForm.TemplateFormID());
            }
        });

        this.viewModelDetail.NavigationOnLoad = ko.toJSON(this.viewModelDetail.Navigation);

        UI.showNotificationMessage(Constants.PHRASE_ITEMSAVED, UI.messageTypeValues.success);
        return true;
    }

    // used to change the isActive value for the navigation item being edited
    private setNavigationIsActive(isActive: boolean): boolean {
        if (this.viewModelDetail.NavigationObservable == null) {
            // this should not happen
            this.unselectAllNavigation();
            this.viewModelDetail.NavigationVisible(false);
            return true;
        }

        this.viewModelDetail.Navigation.IsActive(isActive);
        this.viewModelDetail.NavigationObservable.IsActive(isActive);

        this.viewModelDetail.NavigationOnLoad = ko.toJSON(this.viewModelDetail.Navigation);

        return true;
    }

    private isNavigationValid(): boolean {
        let isValid: boolean = true;
        const navigation: INavigationDetail = this.viewModelDetail.Navigation;

        // if deleted, do no validation
        if (!navigation.IsActive()) { return true; }

        isValid = UI.setRequiredNumber(navigation.NavigationTypeID, navigation.NavigationTypeIDMissing, true) ? isValid : false;
        isValid = UI.setRequiredString(navigation.NavigationName, navigation.NavigationNameMissing) ? isValid : false;

        switch (navigation.NavigationTypeID()) {
            case TemplateData.NAVIGATIONTYPE_FORM:
                isValid = UI.setRequiredNumber(navigation.TemplateFormID, navigation.TemplateFormIDMissing, true) ? isValid : false;
                break;

            case TemplateData.NAVIGATIONTYPE_LIST:
                // nothing to validate
                break;

            case TemplateData.NAVIGATIONTYPE_REPORT:
                // nothing to validate
                break;
        }

        return isValid;
    }

    private addNavigation(): void {
        const navigation: TemplateData.ITemplateNavigation = {
            ID: 0,
            TemplateFormID: 0,
            NavigationTypeID: TemplateData.NAVIGATIONTYPE_FORM,
            NavigationName: "New Navigation",
            NavigationNameTranslations: [], // This will get populated by createNavigationObservable below
            NavigationIcon: "",
            SortOrder: 0,
            IsActive: true,
            ListItems: [],
            Lists: [],
        };
        const navigationObservable: INavigationObservable = this.createNavigationObservable(navigation);

        this.viewModelTree.Navigations.push(navigationObservable);
        this.editNavigation(navigationObservable);
    }

    private moveNavigationItem(navigationObservable: INavigationObservable, moveUp: boolean): void {
        let index: number = this.viewModelTree.Navigations.indexOf(navigationObservable);
        if (index < 0) { return; }
        this.viewModelTree.Navigations.remove(navigationObservable);
        if (moveUp) {
            index = index - 1;
        } else {
            index = index + 1;
        }
        this.viewModelTree.Navigations.splice(index, 0, navigationObservable);
        navigationObservable.ShowActions(false);
    }

    // -------------------------------------------------------------------------
    // list editing functions
    // -------------------------------------------------------------------------

    // loads the specified list item into the detail screen for editing
    private editList(listObservable: IListObservable): void {
        const list: IListDetail = this.viewModelDetail.List;

        list.ID(listObservable.ID());

        list.FormID(listObservable.FormID());
        if (listObservable.HasForm()) {
            list.ListTypeID(this.LISTTYPE_FORM);
        } else {
            list.ListTypeID(this.LISTTYPE_LIST);
        }
        list.ListName(listObservable.ListName());
        list.IsActive(listObservable.IsActive());

        // set the selected forms
        const templateFormIDs: number[] = listObservable.ListItems();
        list.ListItems().forEach((listItem: IListItemObservable) => {
            const selected: boolean = _.includes(templateFormIDs, listItem.TemplateFormID());
            listItem.Selected(selected);
        });

        // clear all errors
        list.ListNameMissing(false);
        list.ListTypeIDMissing(false);
        list.FormIDMissing(false);
        list.ListFormsMissing(false);

        // remember the object we are editing
        this.viewModelDetail.ListObservable = listObservable;
        // save current values so we can see if they changed
        this.viewModelDetail.ListOnLoad = ko.toJSON(this.viewModelDetail.List);

        // update the UI
        this.unselectAllNavigation();
        listObservable.IsSelected(true);

        this.viewModelDetail.ListVisible(true);
        this.viewModelDetail.NavigationVisible(false);
        this.viewModelDetail.SettingsVisibleDetail(false);
        this.viewModelTree.SettingsVisibleTree(false);
        this.viewModelTree.TranslationsVisibleTree(false);

        this.container.find("div[data-id='detailContainer']").find("input:first").focus();
    }

    // saves the list item that is being edited in the detail page
    // into the main tree observable object.
    private saveList(): boolean {
        if (this.viewModelDetail.ListObservable == null) {
            // this should not happen
            this.unselectAllNavigation();
            this.viewModelDetail.ListVisible(false);
            return true;
        }

        if (!this.isListValid()) {
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
            return false;
        }

        const list: IListDetail = this.viewModelDetail.List;
        const listObservable: IListObservable = this.viewModelDetail.ListObservable;

        listObservable.FormID(list.FormID());
        if (list.ListTypeID() === this.LISTTYPE_FORM) {
            listObservable.HasForm(true);
        } else {
            listObservable.HasForm(false);
        }
        listObservable.ListName(list.ListName());

        listObservable.ListItems.removeAll();
        list.ListItems().forEach((listItem: IListItemObservable) => {
            if (listItem.Selected()) {
                listObservable.ListItems.push(listItem.TemplateFormID());
            }
        });

        this.viewModelDetail.ListOnLoad = ko.toJSON(this.viewModelDetail.List);
        UI.showNotificationMessage(Constants.PHRASE_ITEMSAVED, UI.messageTypeValues.success);

        return true;
    }

    // used to change the isActive value for the list item being edited
    private setListIsActive(isActive: boolean): boolean {
        if (this.viewModelDetail.ListObservable == null) {
            // this should not happen
            this.unselectAllNavigation();
            this.viewModelDetail.ListVisible(false);
            return true;
        }

        this.viewModelDetail.List.IsActive(isActive);
        this.viewModelDetail.ListObservable.IsActive(isActive);

        this.viewModelDetail.ListOnLoad = ko.toJSON(this.viewModelDetail.List);

        return true;
    }

    private isListValid(): boolean {
        let isValid: boolean = true;
        const list: IListDetail = this.viewModelDetail.List;

        // if deleted, then do no validation
        if (!list.IsActive()) { return true; }

        isValid = UI.setRequiredString(list.ListTypeID, list.ListTypeIDMissing) ? isValid : false;
        isValid = UI.setRequiredString(list.ListName, list.ListNameMissing) ? isValid : false;

        switch (list.ListTypeID()) {
            case this.LISTTYPE_FORM:
                isValid = UI.setRequiredNumber(list.FormID, list.FormIDMissing, true) ? isValid : false;
                break;

            case this.LISTTYPE_LIST:
                // nothing to validate
                break;
        }

        return isValid;
    }

    private createNewListObservable(): IListObservable {
        const list: TemplateData.ITemplateList = {
            ID: 0,
            FormID: 0,
            HasForm: true,
            HasAttributes: false,
            ListName: "New Item",
            SortOrder: 0,
            IsActive: true,
            ListItems: [],
            Children: [],
        };
        const listObservable: IListObservable = this.createListObservable(list);
        return listObservable;
    }

    private addListItemToList(parentList: IListObservable): void {
        const newListObservable: IListObservable = this.createNewListObservable();
        parentList.Children.push(newListObservable);
        this.editList(newListObservable);
    }

    private addListItemToNavigation(navigation: INavigationObservable): void {
        const newListObservable: IListObservable = this.createNewListObservable();
        navigation.Lists.push(newListObservable);
        this.editList(newListObservable);
    }

    private moveListItem(listObservableToMove: IListObservable, moveUp: boolean): void {
        // a list item can be under a Navigation item, or under a List item
        // need to figure out which one, then do the move

        // check navigation items first
        const me: TemplateController = this;
        let itemFound: boolean = false;

        this.viewModelTree.Navigations().forEach((navigation: INavigationObservable) => {
            if (itemFound) { return; }

            let index: number = navigation.Lists.indexOf(listObservableToMove);
            if (index >= 0) {
                // item is under this navigation item
                navigation.Lists.remove(listObservableToMove);
                if (moveUp) {
                    index = index - 1;
                } else {
                    index = index + 1;
                }
                navigation.Lists.splice(index, 0, listObservableToMove);
                listObservableToMove.ShowActions(false);
                itemFound = true;
            } else {
                // check any children
                navigation.Lists().forEach((list: IListObservable) => {
                    if (itemFound) { return; }
                    if (list.Children().length === 0) { return; }
                    itemFound = me.moveListItemForList(listObservableToMove, moveUp, list);
                });
            }
        });
    }

    private moveListItemForList(listObservableToMove: IListObservable, moveUp: boolean, parentList: IListObservable): boolean {
        const me: TemplateController = this;
        let itemFound: boolean = false;

        let index: number = parentList.Children.indexOf(listObservableToMove);
        if (index >= 0) {
            // found the item, so move it
            parentList.Children.remove(listObservableToMove);
            if (moveUp) {
                index = index - 1;
            } else {
                index = index + 1;
            }
            parentList.Children.splice(index, 0, listObservableToMove);
            listObservableToMove.ShowActions(false);
            return true;
        }

        // check any children lists
        parentList.Children().forEach((child: IListObservable) => {
            if (itemFound) { return; }
            if (child.Children().length === 0) { return; }

            itemFound = me.moveListItemForList(listObservableToMove, moveUp, child);
        });

        return itemFound;
    }

    // -------------------------------------------------------------------------
    // functions for getting the data structure needed
    // to update the template on the server
    // -------------------------------------------------------------------------

    private getTemplate(): TemplateData.ITemplate {
        const template: TemplateData.ITemplate = {
            ID: this.viewModelHeader.ID(),
            TemplateName: this.viewModelHeader.TemplateName(),
            TargetTemplateID: this.viewModelHeader.TargetTemplateID(),
            IsActive: this.viewModelHeader.IsActive(),
            Navigations: this.getTemplateNavigations(),
            NotifyUserOnAssignment: this.viewModelDetail.Settings.NotifyUserOnAssignment(),
            LinkedCustomerDocuments: this.getLinkedCustomerDocuments(),
            TemplateRoles: this.getTemplateRoles(),
            ReportID: this.viewModelDetail.Settings.ReportID(),
            TemplateSettings: this.getTemplateSettingObjects(),
        };

        return template;
    }

    private getTemplateNavigations(): TemplateData.ITemplateNavigation[] {
        const me: TemplateController = this;
        const templateNavigations: TemplateData.ITemplateNavigation[] = [];

        this.viewModelTree.Navigations().forEach((navigation: INavigationObservable) => {
            const templateNavigation: TemplateData.ITemplateNavigation = {
                ID: navigation.ID(),
                TemplateFormID: navigation.TemplateFormID(),
                NavigationTypeID: navigation.NavigationTypeID(),
                NavigationName: navigation.NavigationName(),
                NavigationNameTranslations: me.createTranslationTextsFromObservables(navigation.NavigationNameTranslations),
                NavigationIcon: navigation.NavigationIcon(),
                IsActive: navigation.IsActive(),
                ListItems: me.getTemplateListItems(navigation.ListItems),
                Lists: me.getTemplateLists(navigation.Lists),
            };
            templateNavigations.push(templateNavigation);
        });

        return templateNavigations;
    }

    private getTemplateListItems(listItems: KnockoutObservableArray<number>): TemplateData.ITemplateListItem[] {
        const templateListItems: TemplateData.ITemplateListItem[] = [];

        listItems().forEach((listItem: number) => {
            const templateListItem: TemplateData.ITemplateListItem = {
                TemplateFormID: listItem,
            };
            templateListItems.push(templateListItem);
        });

        return templateListItems;
    }

    private getTemplateLists(lists: KnockoutObservableArray<IListObservable>): TemplateData.ITemplateList[] {
        const me: TemplateController = this;
        const templateLists: TemplateData.ITemplateList[] = [];

        lists().forEach((list: IListObservable) => {
            const templateList: TemplateData.ITemplateList = {
                ID: list.ID(),
                ListName: list.ListName(),
                HasForm: list.HasForm(),
                HasAttributes: list.HasAttributes(),
                IsActive: list.IsActive(),
                Children: me.getTemplateLists(list.Children),
                ListItems: me.getTemplateListItems(list.ListItems),
                FormID: list.FormID(),
                AttributeFormID: list.AttributeFormID(),
            };
            templateLists.push(templateList);
        });

        return templateLists;
    }

    private getLinkedCustomerDocuments(): TemplateData.ITemplateLinkedCustomerDocument[] {
        const docs: TemplateData.ITemplateLinkedCustomerDocument[] = [];

        this.viewModelDetail.Settings.LinkedCustomerDocuments().forEach((doc) => {
            docs.push({
                Description: doc.Description(),
                FileName: doc.FileName(),
                TemplateCustomerDocumentID: doc.TemplateCustomerDocumentID(),
                CustomerDocumentID: doc.CustomerDocumentID(),
                IsActive: doc.IsActive(),
                StorageKey: doc.StorageKey(),
            });
        });

        return docs;
    }

    private getTemplateRoles(): ITemplateAccessRoleTemplate[] {
        const roles: ITemplateAccessRoleTemplate[] = [];

        for (const r of this.viewModelDetail.Settings.TemplateRoles()) {
            roles.push({
                ID: r.ID(),
                IsActive: r.IsActive(),
                TemplateAccessRoleID: r.TemplateAccessRoleID(),
                TemplateID: r.TemplateID(),
                TemplateAccessRoleName: r.TemplateAccessRoleName(),
                TemplateName: r.TemplateName(),
            });
        }

        return roles;
    }

    private getTemplateSettingObjects(): TemplateData.ITemplateSetting[] {
        const templateSettings: TemplateData.ITemplateSetting[] = [];

        const observableToObject = (templateSettingObservable: ITemplateSettingObservable): TemplateData.ITemplateSetting => {
            const templateSetting: TemplateData.ITemplateSetting = {
                ID: templateSettingObservable.ID(),
                TemplateSettingTypeID: templateSettingObservable.TemplateSettingTypeID(),
                Value: templateSettingObservable.Value(),
                IsActive: templateSettingObservable.IsActive(),
                SettingKey: templateSettingObservable.SettingKey(),
                SettingType: templateSettingObservable.SettingType(),
            };
            return templateSetting;
        };

        for (const templateSettingObservable of this.viewModelDetail.Settings.SingleInstanceSettings()) {
            templateSettings.push(observableToObject(templateSettingObservable));
        }
        for (const templateSettingObservable of this.viewModelDetail.Settings.ReportEmailSettings()) {
            templateSettings.push(observableToObject(templateSettingObservable));
        }
        for (const templateSettingObservable of this.viewModelDetail.Settings.MobileTaskSettingsList()) {
            templateSettings.push(observableToObject(templateSettingObservable));
        }

        return templateSettings;
    }

    // -------------------------------------------------------------------------
    // uI functions
    // -------------------------------------------------------------------------

    // returns true if the data in the detail screen has been modified
    // by the user.
    // work for both the navigation and list fields
    private hasDetailDataChanged(): boolean {
        let hasChanged: boolean = false;
        let fieldValues: string = "";

        if (this.viewModelDetail.NavigationVisible()) {
            fieldValues = ko.toJSON(this.viewModelDetail.Navigation);
            hasChanged = (fieldValues !== this.viewModelDetail.NavigationOnLoad);
        } else if (this.viewModelDetail.ListVisible()) {
            fieldValues = ko.toJSON(this.viewModelDetail.List);
            hasChanged = (fieldValues !== this.viewModelDetail.ListOnLoad);
        }
        // else, if no form is visible, then nothing has changed

        return hasChanged;
    }

    // this function is called when the user is changing what is
    // dispayed in the detail section (navigation or list) .
    // if the detail section has invalid values, will display an error
    // message and not let the user continue.
    // if the detail section has unsaved changes, will ask the
    // user if they want to continue
    private confirmDetailChange(onConfirmed: () => void): void {
        if (this.viewModelDetail.NavigationVisible() && !this.isNavigationValid()) {
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
        } else if (this.viewModelDetail.ListVisible() && !this.isListValid()) {
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
        } else if (this.hasDetailDataChanged()) {
            // auto save any changes
            if (this.viewModelDetail.NavigationVisible()) {
                if (this.saveNavigation()) {
                    onConfirmed();
                }
            } else if (this.viewModelDetail.ListVisible()) {
                if (this.saveList()) {
                    onConfirmed();
                }
            } else {
                onConfirmed();
            }
        } else {
            // data is valid and nothing has changed
            onConfirmed();
        }
    }

    // used to update the URL when a new record is added
    private updateURL(newID: number, templateName: string): void {
        const app: IApplication = Global.app();
        const routeChange: EventUtil.IRouteChange = {
            appRouterID: this.appRouterID([]),
            url: "/Template/" + newID,
            title: this.title(),
            subTitle: templateName,
            newAppRouterID: this.appRouterID([newID]),
        };

        app.events().trigger(EventUtil.EVENT_ROUTECHANGE, routeChange);
    }

    private clearSearch(): void {
        const me: TemplateController = this;
        me.viewModelDetail.Navigation.searchFormName("");
        me.viewModelDetail.List.searchListItemFormName("");
    }
    private unselectAllNavigation(): void {
        const me: TemplateController = this;

        this.viewModelTree.Navigations().forEach((navigation: INavigationObservable) => {
            navigation.IsSelected(false);
            if (navigation.Lists().length > 0) {
                me.unselectAllLists(navigation.Lists);
            }
        });
    }

    private unselectAllLists(lists: KnockoutObservableArray<IListObservable>): void {
        const me: TemplateController = this;

        lists().forEach((list: IListObservable) => {
            list.IsSelected(false);
            if (list.Children().length > 0) {
                me.unselectAllLists(list.Children);
            }
        });
    }

    private DeleteReportEmailSetting(selectedCustomerSetting: ITemplateSettingObservable) {
        const me: TemplateController = this;

        if (selectedCustomerSetting.ID() === 0) {
            me.viewModelDetail.Settings.ReportEmailSettings.remove(selectedCustomerSetting);
        } else {
            selectedCustomerSetting.IsActive(false);
        }
    }

    private DeleteMobileSetting(selectedSetting: ITemplateSettingObservable) {
        const me: TemplateController = this;
        if (selectedSetting.ID() === 0) {
            me.viewModelDetail.Settings.MobileTaskSettingsList.remove(selectedSetting);
        } else {
            selectedSetting.IsActive(false);
        }
    }

    private createEmptyFormTextTranslations(): ITranslatedTextObservable[] {
        const userSession = Global.app().userSession;
        if (!userSession || !userSession.Languages) {
            return [];
        }

        return userSession.Languages.map((language) => {
            const translatedTextObservable: ITranslatedTextObservable = {
                LanguageID: language.ID,
                LanguageName: language.Name,
                TranslatedText: ko.observable(""),
            };
            return translatedTextObservable;
        });
    }

    private createTranslationTextsFromObservables(translatedTextObservables: KnockoutObservableArray<ITranslatedTextObservable>): ITranslatedText[] {
        return translatedTextObservables().map((translatedTextObservable) => {
            const translatedText: ITranslatedText = {
                LanguageID: translatedTextObservable.LanguageID,
                TranslatedText: translatedTextObservable.TranslatedText(),
            };
            return translatedText;
        });
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/template.ts